import React from 'react';
import { t } from '@citrite/translate';
import {
	Button,
	CustomBadge,
	fromThemeColors,
	IInCircleSolidIcon,
	layout,
	palette,
	styleUtils,
	Text,
	typography,
	withModals,
	WithModalsProps,
} from '@citrite/web-ui-component';
import { SkeletonLoaderShapes } from '@citrite/workspace-ui-platform-react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import moment from 'moment';
import { PowerState } from 'App/Activity/Network/ActionsUtil';
import { useDesktopDetails } from 'App/Activity/useDesktopDetails';
import { useResourceState } from 'App/Activity/useResourceState';
import { GenericErrorBoundary } from 'App/GenericErrorBoundary';
import { DesktopIcon } from 'App/Tile/DesktopIcon';
import { FavoriteIconWithRef } from 'App/Tile/FavoriteIcon';
import { DivWrapper } from 'App/Tile/GenericTile';
import { PowerStateBadge } from 'App/Tile/PowerStateBadge';
import { ResourceMenu } from 'App/Tile/ResourceMenu';
import { useBrowserExtensionPrompt } from 'App/Tile/useBrowserExtensionPrompt';
import { useResourceContextMenuBuilder } from 'App/Tile/useResourceContextMenuBuilder';
import {
	getDesktopWallpaper,
	launchResourceOnClick,
	shouldShowTroubleshootOption,
} from 'App/Tile/utils';
import { useConfigurationContext } from 'Configuration/useConfigurationContext';
import { FeatureFlag } from 'Environment/FeatureFlag';
import { LaunchStatus } from 'Environment/launchResource/launchResource';
import { detectLanguage } from 'javascript/sf/detectLanguage';
import { useFeatureCanary } from 'utils/useFeatureCanary';
import { useLoadableResourceContext } from 'Workspace/ResourceProvider';
import { Resource } from 'Workspace/ResourceProvider/resourceTypes';
import { useUserContext } from 'Workspace/UserContext';

const { LoadingBar } = SkeletonLoaderShapes;

const TextWithFallback = styled(Text)`
	&:empty::after {
		content: '\u00A0';
		visibility: hidden;
	}
`;

const buttonWrapperStyle = {
	width: '100%',
};

const StyledDiv = styled.div<{ isResourceAvailable: boolean; isGrayout: boolean }>`
	display: flex;
	flex-direction: column;
	min-width: auto;
	height: 352px;
	border-radius: ${layout.space12};
	border: 1px solid ${palette.grey300};
	background: ${fromThemeColors('primaryContainerBackground')};
	cursor: pointer;

	${({ isGrayout }) =>
		isGrayout
			? css`
					box-shadow: ${styleUtils.shadow1.boxShadow};
					&:hover {
						box-shadow: ${styleUtils.shadow2.boxShadow};
					}
					.resource-icon-image {
						filter: grayscale(100%) saturate(0) brightness(92%) contrast(88%)
							drop-shadow(0 1px 0 rgba(0, 0, 0, 0.12))
							drop-shadow(0 0 2px rgba(0, 0, 0, 0.08));
					}
			  `
			: css`
					box-shadow: ${styleUtils.shadow2.boxShadow};
					&:hover {
						transition: box-shadow 0.1s linear;
						box-shadow: ${styleUtils.shadow3.boxShadow};
					}
			  `}

	${({ isResourceAvailable }) =>
		!isResourceAvailable &&
		css`
			opacity: 0.5;
			cursor: not-allowed;
			box-shadow: ${styleUtils.shadow1.boxShadow};
			&:hover {
				box-shadow: ${styleUtils.shadow1.boxShadow};
			}
			* {
				pointer-events: none;
			}
		`}
`;

const StyledImageContainer = styled.div<{ wallpaper: string }>`
	position: relative;
	flex: 1;
	background-image: url(${props => props.wallpaper});
	border-radius: ${layout.smallSpace};
	background-size: cover;
	background-position: center;
`;

const HeaderWrapper = styled.div`
	display: flex;
	flex: 1;
	padding: ${layout.smallSpace} ${layout.smallSpace} 0;
`;

const FooterWrapper = styled.div<{ isGrayout?: boolean }>`
	height: 140px;
	padding: ${layout.mediumSpace};
	display: flex;
	flex-direction: column;

	${({ isGrayout }) =>
		isGrayout &&
		css`
			& ${TitleText}, {
				color: ${palette.grey700};
			}

			& ${SubTitleText} {
				color: ${palette.grey600};
			}
		`}
`;

const TitleText = styled(TextWithFallback)`
	font-size: ${typography.sizes.base};
	font-weight: ${typography.weights.regular};
	color: ${palette.black};
`;

const SubTitleText = styled(TextWithFallback)`
	font-size: ${typography.sizes.sml2};
	font-weight: ${typography.weights.semiBold};
	color: ${palette.grey800};
	margin-top: ${layout.smallSpace};
`;

const LastOpenedText = styled(TextWithFallback)`
	font-size: ${typography.sizes.xsml};
	font-weight: ${typography.weights.regular};
	color: ${palette.grey700};
	margin-top: ${layout.space14};
`;

const MaintenanceWrapper = styled.div`
	display: flex;
	align-items: flex-start;
	gap: ${layout.space4};
	margin-top: auto;
`;

const MaintenanceModeReason = styled(Text)`
	font-size: ${typography.sizes.xsml};
	color: ${palette.grey900};
	font-weight: ${typography.weights.regular};
`;

const DetailsWrapper = styled.div`
	position: relative;
	.resource-menu-container {
		top: -${layout.smallSpace};
		right: 0;
	}
	.details-texts {
		display: flex;
		flex-direction: column;
	}
`;

const TileStateOSWrapper = styled.div`
	position: absolute;
	bottom: 0;
	left: 0;
	background: ${fromThemeColors('primaryContainerBackground')};
	padding: ${layout.tinySpace} ${layout.tinySpace} 0 0;
	border-radius: 0 ${layout.smallSpace};
	display: flex;
	flex-direction: row;
	align-items: center;
	overflow: hidden;
	gap: ${layout.tinySpace};
`;

const StyledCustomBadge = styled(CustomBadge)`
	height: ${layout.space22};
	font-size: ${typography.sizes.xsml2};
	color: ${palette.grey900};
	line-height: normal;
	border: 1px solid ${palette.grey300};
	padding: ${layout.tinySpace} ${layout.space6};
	background-color: ${palette.grey100};
`;

interface TileHeaderProps {
	resource: Resource;
	favoriteRef: React.RefObject<HTMLInputElement>;
	canShowTroubleshootStatus: boolean;
}
const TileHeader = (props: TileHeaderProps) => {
	const { resource, favoriteRef, canShowTroubleshootStatus } = props;
	const { value: resourceContext } = useLoadableResourceContext();
	const hasSubscriptionsEnabled = resourceContext.subscriptionsEnabled();
	const {
		desktopWallpaper,
		desktopOS,
		isLoading: isMachineListLoading,
	} = useDesktopDetails(resource);
	const { state, lastUpdatedTime } = useResourceState(resource);

	const isLoading = state === PowerState.LOADING || isMachineListLoading;
	const shouldShowOSBadge = desktopOS && !isLoading;

	return (
		<HeaderWrapper>
			<StyledImageContainer wallpaper={getDesktopWallpaper(desktopWallpaper)}>
				{hasSubscriptionsEnabled && (
					<FavoriteIconWithRef resource={resource} ref={favoriteRef} />
				)}
				<DesktopIcon resource={resource} />
				<TileStateOSWrapper>
					<PowerStateBadge
						resource={resource}
						powerState={state}
						lastUpdatedTime={lastUpdatedTime}
						canShowTroubleshootStatus={canShowTroubleshootStatus}
					/>
					{shouldShowOSBadge && <StyledCustomBadge>{desktopOS}</StyledCustomBadge>}
				</TileStateOSWrapper>
			</StyledImageContainer>
		</HeaderWrapper>
	);
};

interface TileFooterProps extends WithModalsProps {
	resource: Resource;
	favoriteRef: React.RefObject<HTMLInputElement>;
}
const TileFooter = (props: TileFooterProps) => {
	const { resource, showModal, favoriteRef } = props;
	const {
		isLoading,
		lastLaunchedTime,
		maintenanceModeUserString,
		isUnderMaintenanceOrUnavailable,
	} = useDesktopDetails(resource);
	const contextMenu = useResourceContextMenuBuilder({
		resource,
		favoriteRef,
		showModal,
	});
	const openedAgo = React.useMemo(() => {
		if (!lastLaunchedTime) {
			return null;
		}
		const openedTime = moment(lastLaunchedTime).locale(detectLanguage()).fromNow();
		return t('Workspace:opened_at', { time: openedTime });
	}, [lastLaunchedTime]);

	return (
		<FooterWrapper isGrayout={isUnderMaintenanceOrUnavailable}>
			<DetailsWrapper>
				<div className="details-texts">
					<TitleText>{resource.name}</TitleText>
					<SubTitleText>{resource.desktophostname}</SubTitleText>

					<LastOpenedText>
						{isLoading ? <LoadingBar width="60%" /> : openedAgo}
					</LastOpenedText>
				</div>

				<ResourceMenu resource={resource} contextMenuBuilder={() => contextMenu} />
			</DetailsWrapper>
			{maintenanceModeUserString && !isLoading && (
				<MaintenanceWrapper>
					<IInCircleSolidIcon color={palette.blue800} width={12} height={14} />
					<MaintenanceModeReason>{maintenanceModeUserString}</MaintenanceModeReason>
				</MaintenanceWrapper>
			)}
		</FooterWrapper>
	);
};

interface Props {
	resource: Resource;
	isResourceAvailable: boolean;
}

interface __Props extends Props, WithModalsProps {}

function __DetailedDesktopTile({ resource, isResourceAvailable, showModal }: __Props) {
	const shouldWrapButtonComponent = !useFeatureCanary(
		FeatureFlag.DisableResourceButtonWrap
	);
	const favoriteRef = React.useRef(null);
	const { isUnderMaintenanceOrUnavailable } = useDesktopDetails(resource);
	const { value: resourceContext } = useLoadableResourceContext();
	const { workspaceConfiguration } = useConfigurationContext();
	const { userDetails } = useUserContext();
	const canShowBrowserExtensionPrompt = useBrowserExtensionPrompt();

	const canShowTroubleshootStatus =
		shouldShowTroubleshootOption(resource, workspaceConfiguration) &&
		resourceContext?.resourceLatestStatus?.[resource.id]?.status === LaunchStatus.Failed;

	const handleLaunchResource = () =>
		launchResourceOnClick({
			workspaceConfiguration,
			resource,
			resourceContext,
			userDetails,
			canShowBrowserExtensionPrompt,
			showModal,
		});
	const Wrapper = shouldWrapButtonComponent ? Button.Wrapper : DivWrapper;

	return (
		<Wrapper
			disabled={!isResourceAvailable}
			onClick={handleLaunchResource}
			style={buttonWrapperStyle}
		>
			<StyledDiv
				isResourceAvailable={isResourceAvailable}
				isGrayout={isUnderMaintenanceOrUnavailable}
			>
				<TileHeader
					resource={resource}
					favoriteRef={favoriteRef}
					canShowTroubleshootStatus={canShowTroubleshootStatus}
				/>
				<TileFooter resource={resource} showModal={showModal} favoriteRef={favoriteRef} />
			</StyledDiv>
		</Wrapper>
	);
}

const _DetailedDesktopTile = withModals(__DetailedDesktopTile);

export const DetailedDesktopTile = (props: Props) => {
	return (
		<GenericErrorBoundary
			renderProp={() => <_DetailedDesktopTile {...props} />}
			name="DetailedDesktopTile"
		/>
	);
};
